/**
 * HUD values and layout, both attach-UI layouts (W1-06; R§7, F§10.5, S§0).
 *
 * UI space: x in [-1, 1], y in [-1/ar, 1/ar], y DOWN, inside the letterboxed viewport. top = -1/ar,
 * eps = 0.02/ar, p = HUD slide progress (1 in the editor), slide = (1 - p) * 0.4. Text sizes are factors of the
 * glyph PxScale 0.04 * size * W px = 0.08 * size UI units; a text box is `ascent` high and anchored on
 * (ascent line .. baseline): anchor y 0 puts the ascent line at pos.y, anchor y 1 the baseline.
 *
 * Layout with useAttachUiFix (R§7.2 table):
 *   pause        two rects 0.015 x 0.048 around c = (-0.94, top + 3.5 eps - slide + 0.024); pivots (c.x - 0.0225, c.y - 0.024)
 *   score        "%07d" at (0.97, top + 2.2 eps - slide), anchor (1, 0), size 0.8, PGR font; pivots = pos
 *   accuracy     "%05.2f%" at (0.97, scoreTop + 0.07), anchor (1, 0), size 0.4, alpha * 0.7 (player showAcc, see
 *                showAccuracyOf; hidden by default)
 *   combonumber  (combo >= 3) at (0, comboY), anchor (0.5, 0.5), size 1.0;
 *                comboY = top + 2 eps - slide + ascUi(1.0) / 2 (measured with the UI font, drawn with PGR: quirk)
 *   combo        (combo >= 3) "AUTOPLAY" in autoplay, "COMBO" in play, at (0, comboBottom + 0.01 + ascUi(0.4) / 2),
 *                anchor (0.5, 0.5), size 0.4; comboBottom = bottom of the drawn number box
 *   name         (-0.97, 1/ar - 2.8 eps + slide), anchor (0, 1), size 0.5, UI font, max width 0.8
 *   level        (0.97, same y), anchor (1, 1), size 0.5, UI font
 *   bar          rect (-1, top, d, eps) white 0.6 + tick (-1 + d - 0.003, top, 0.006, eps) white,
 *                d = clamp(2 t / trackLength, 0, 2); fixed colour; pivot (-1, top + eps / 2)
 * Legacy layout (useAttachUiFix false): the combo number sits at (0, top + 2 eps - slide) with anchor (0.5, 0) and
 * "COMBO" at (0, numberBottom + 0.01) with anchor (0.5, 0); score, name and level SCALE about their text centres,
 * the pause button about its centre; rotation pivots are unchanged (R§7.2 last bullet).
 *
 * Attach transform (R§7.3): for the highest-index line attached to an element,
 *   M = Translate(x, -y) * RotateAbout(rotPivot, +rpeRotate own) * ScaleAbout(scalePivot, sx, sy)
 * with the line's composed position (parents included), its OWN rotation only, its scale factors without the
 * x0.5 rule (LineStateRt.sx/sy), alpha max(lineAlpha, 0) (a line without alpha events hides the element) and colour
 * from colour events or white; the bar ignores the colour.
 */

import { Mat2 } from '../../math/mat2d';
import type { MutMat2D, RGBAf } from '../../util/types';
import type { UiElement } from '../../model/types';
import type {
  EngineCompileContext,
  FrameOptions,
  HudDraw,
  HudElementDraw,
  HudValues,
  JudgeView,
  LineStateRt,
  PhiraPlayerSettings,
  Viewport,
} from '../types';
import { Search } from '../../math/search';
import { judgedCounts } from './judgedHud';
import { lineRt, realNoteIndex } from './store';

/** UI units per text size unit: PxScale 0.04 * size * W px over W/2 px per unit. */
export const HUD_EM = 0.08;
/** phigros.ttf ascent / PxScale (USE_TYPO_METRICS, typo 800 / -200, upm 1000). */
export const PGR_FONT_ASCENT = 0.8;
/**
 * UI font ascent / PxScale. Phira's font.ttf is not available; the editor's UI font is Noto Sans SC, whose hhea
 * metrics (ascender 1160, descender -288, upm 1000, no USE_TYPO_METRICS) give 1160 / 1448.
 */
export const UI_FONT_ASCENT = 1160 / 1448;

/** Text box height (= ascent) of a font at a size, in UI units. */
export function ascentOf(font: 'pgr' | 'ui', size: number): number {
  return HUD_EM * size * (font === 'pgr' ? PGR_FONT_ASCENT : UI_FONT_ASCENT);
}

/**
 * Width of a single-line text in UI units (used only for the legacy layout's text-centre scale pivots). Called as a
 * plain function (no `this`).
 */
export type HudTextMeasurer = (text: string, font: 'pgr' | 'ui', size: number) => number;

/**
 * `FrameOptions.measureText` (contract, CR-W1-06-2) is the renderer's text measurer: W2-01a measures with the real
 * fonts and W2-02 puts the measurer on the frame options it builds, so every engine and every frame uses the
 * measurer it is given (no shared state). Without one, widths are estimated from average advances
 * (estimateTextWidth). Only the legacy layout (useAttachUiFix false) needs a width; the default layout, which every
 * export writes, never measures.
 */

/** Average-advance estimate of a text width: 0.55 em per Latin character, 1 em per CJK one. */
export function estimateTextWidth(text: string, _font: 'pgr' | 'ui', size: number): number {
  let em = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    em += cp >= 0x2e80 ? 1 : 0.55;
  }
  return em * HUD_EM * size;
}

/**
 * The text measurer of a frame: `opts.measureText` when the options carry one (its result is used when it is a
 * finite width >= 0, the estimate otherwise), else estimateTextWidth.
 */
export function textMeasurerOf(opts: FrameOptions): HudTextMeasurer {
  const m = opts.measureText;
  if (m === undefined) return estimateTextWidth;
  return (text, font, size) => {
    const w = m(text, font, size);
    return Number.isFinite(w) && w >= 0 ? w : estimateTextWidth(text, font, size);
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------------------------------------------

/**
 * Autoplay HUD values: every real note is a Perfect committed at its time (holds at their end); combo = committed
 * count k, score = round(k / N * 1e6) (exactly 1 000 000 at the end), progress = k / N (judging progress, 0..1).
 */
export function autoplayHudValues(ctx: EngineCompileContext, t: number): HudValues {
  const idx = realNoteIndex(ctx);
  const total = idx.commit.length;
  const k = Search.upperBound(idx.commit, t);
  return {
    score: total === 0 ? 0 : Math.round((k / total) * 1e6),
    combo: k,
    totalNotes: total,
    progress: total === 0 ? 0 : k / total,
  };
}

/** HUD numbers of a frame (values plus accuracy and the max combo of judged play). */
export interface HudNumbers extends HudValues {
  /** 0..1. */
  readonly accuracy: number;
  readonly maxCombo: number;
}

/**
 * Judged-play values from the JudgeView: notes commit at their judgement (holds that are not missed at their end);
 * combo counts consecutive Perfect/Good commits; score = round(900000 (P + 0.65 G) / N + 100000 maxCombo / N)
 * (the Phigros scoring Phira uses); accuracy = (P + 0.65 G) / judged. Maintained incrementally per JudgeView
 * (judgedHud.ts): a frame polls only the notes near t and the holds being held, no per-frame sort.
 */
export function judgedHudValues(ctx: EngineCompileContext, t: number, judge: JudgeView): HudNumbers {
  const c = judgedCounts(ctx, t, judge);
  const total = c.total;
  const weighted = c.perfect + 0.65 * c.good;
  return {
    score: total === 0 ? 0 : Math.round((9e5 * weighted) / total + (1e5 * c.maxCombo) / total),
    combo: c.combo,
    totalNotes: total,
    progress: total === 0 ? 0 : c.committed / total,
    accuracy: c.committed === 0 ? 1 : weighted / c.committed,
    maxCombo: c.maxCombo,
  };
}

/** Phira's `showAcc` (accuracy under the score): PhiraPlayerSettings.showAccuracy, hidden by default like Phira. */
export function showAccuracyOf(player: PhiraPlayerSettings): boolean {
  return player.showAccuracy === true;
}

/** "%07d" */
export function formatScore(score: number): string {
  return String(Math.max(0, Math.trunc(score))).padStart(7, '0');
}

/** "%05.2f%" of a 0..1 accuracy. */
export function formatAccuracy(acc: number): string {
  return `${(acc * 100).toFixed(2).padStart(5, '0')}%`;
}

// ---------------------------------------------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------------------------------------------

/** Line state driving a HUD element. */
type Attach = LineStateRt;

/** For every UI element the attached line state (the highest export index wins). */
export function attachments(ctx: EngineCompileContext, states: readonly LineStateRt[]): Map<UiElement, Attach> {
  const out = new Map<UiElement, Attach>();
  for (const s of states) {
    if (!s.uiMover) continue;
    const el = lineRt(ctx, s.line).ir.attachUI;
    if (el === null) continue;
    const prev = out.get(el);
    if (prev === undefined || s.index >= prev.index) out.set(el, s);
  }
  return out;
}

/** Attach matrix of an element (identity without an attached line). */
function attachMatrix(
  a: Attach | undefined,
  rotPivot: readonly [number, number],
  scalePivot: readonly [number, number],
): MutMat2D {
  const m = Mat2.identity();
  if (a === undefined) return m;
  const s = a;
  Mat2.translate(m, s.x, -s.y, m);
  Mat2.translate(m, rotPivot[0], rotPivot[1], m);
  Mat2.rotateDeg(m, -s.ownRot, m);
  Mat2.translate(m, -rotPivot[0], -rotPivot[1], m);
  Mat2.translate(m, scalePivot[0], scalePivot[1], m);
  Mat2.scale(m, s.sx, s.sy, m);
  Mat2.translate(m, -scalePivot[0], -scalePivot[1], m);
  return m;
}

function attachColor(a: Attach | undefined, alphaMul = 1): RGBAf {
  if (a === undefined) return [1, 1, 1, alphaMul];
  const s = a;
  const alpha = Math.max(s.alpha, 0) * alphaMul;
  const c = s.color;
  return c === null ? [1, 1, 1, alpha] : [c[0], c[1], c[2], alpha];
}

function attachAlpha(a: Attach | undefined): number {
  return a === undefined ? 1 : Math.max(a.alpha, 0);
}

/** Inputs of the layout (separated from the engine for tests). */
export interface HudLayoutInput {
  readonly ar: number;
  readonly p: number;
  readonly attachFix: boolean;
  readonly autoplay: boolean;
  readonly show: boolean;
  readonly t: number;
  readonly trackLengthSec: number;
  readonly numbers: HudNumbers;
  readonly name: string;
  readonly level: string;
  readonly showAccuracy: boolean;
  /** Text widths for the legacy layout's scale pivots (default: estimateTextWidth). */
  readonly measureText?: HudTextMeasurer;
}

/** Builds the HUD elements from the layout inputs and the attachments. */
export function layoutHud(input: HudLayoutInput, attach: ReadonlyMap<UiElement, Attach>): HudElementDraw[] {
  const { ar, p, attachFix, numbers } = input;
  const top = -1 / ar;
  const eps = 0.02 / ar;
  const slide = (1 - p) * 0.4;
  const show = input.show;
  const measure = input.measureText ?? estimateTextWidth;
  const els: HudElementDraw[] = [];

  // Pause button.
  {
    const a = attach.get('pause');
    const cx = -0.94;
    const cy = top + 3.5 * eps - slide + 0.024;
    const color = attachColor(a);
    const pivot: [number, number] = [cx - 0.0225, cy - 0.024];
    els.push({
      id: 'pause',
      visible: show,
      pos: [cx, cy],
      anchor: [0.5, 0.5],
      rects: [
        [cx - 0.0225, cy - 0.024, 0.015, 0.048, color],
        [cx - 0.0225 + 0.03, cy - 0.024, 0.015, 0.048, color],
      ],
      m: attachMatrix(a, pivot, attachFix ? pivot : [cx, cy]),
      color,
    });
  }

  // Combo number and label.
  {
    const showCombo = show && numbers.combo >= 3;
    const numText = String(numbers.combo);
    const label = input.autoplay ? 'AUTOPLAY' : 'COMBO';
    const an = attach.get('combonumber');
    const al = attach.get('combo');
    if (attachFix) {
      const comboY = top + 2 * eps - slide + ascentOf('ui', 1) / 2;
      const numberBottom = comboY + ascentOf('pgr', 1) / 2;
      const labelY = numberBottom + 0.01 + ascentOf('ui', 0.4) / 2;
      els.push({
        id: 'combonumber',
        visible: showCombo,
        text: numText,
        font: 'pgr',
        size: 1,
        pos: [0, comboY],
        anchor: [0.5, 0.5],
        m: attachMatrix(an, [0, comboY], [0, comboY]),
        color: attachColor(an),
      });
      els.push({
        id: 'combo',
        visible: showCombo,
        text: label,
        font: 'pgr',
        size: 0.4,
        pos: [0, labelY],
        anchor: [0.5, 0.5],
        m: attachMatrix(al, [0, labelY], [0, labelY]),
        color: attachColor(al),
      });
    } else {
      const numberY = top + 2 * eps - slide;
      const labelY = numberY + ascentOf('pgr', 1) + 0.01;
      els.push({
        id: 'combonumber',
        visible: showCombo,
        text: numText,
        font: 'pgr',
        size: 1,
        pos: [0, numberY],
        anchor: [0.5, 0],
        m: attachMatrix(an, [0, numberY], [0, numberY]),
        color: attachColor(an),
      });
      els.push({
        id: 'combo',
        visible: showCombo,
        text: label,
        font: 'pgr',
        size: 0.4,
        pos: [0, labelY],
        anchor: [0.5, 0],
        m: attachMatrix(al, [0, labelY], [0, labelY]),
        color: attachColor(al),
      });
    }
  }

  // Score and accuracy (accuracy is part of the score group).
  {
    const a = attach.get('score');
    const scoreTop = top + 2.2 * eps - slide;
    const text = formatScore(numbers.score);
    const pivot: [number, number] = [0.97, scoreTop];
    const scalePivot: [number, number] = attachFix
      ? pivot
      : [0.97 - measure(text, 'pgr', 0.8) / 2, scoreTop + ascentOf('pgr', 0.8) / 2];
    const m = attachMatrix(a, pivot, scalePivot);
    els.push({
      id: 'score',
      visible: show,
      text,
      font: 'pgr',
      size: 0.8,
      pos: [0.97, scoreTop],
      anchor: [1, 0],
      m,
      color: attachColor(a),
    });
    els.push({
      id: 'accuracy',
      visible: show && input.showAccuracy,
      text: formatAccuracy(numbers.accuracy),
      font: 'pgr',
      size: 0.4,
      pos: [0.97, scoreTop + 0.07],
      anchor: [1, 0],
      m,
      color: attachColor(a, 0.7),
    });
  }

  // Progress bar.
  {
    const a = attach.get('bar');
    const len = input.trackLengthSec;
    const d = len > 0 ? Math.min(2, Math.max(0, (2 * input.t) / len)) : 0;
    const alpha = attachAlpha(a);
    const pivot: [number, number] = [-1, top + eps / 2];
    els.push({
      id: 'bar',
      visible: show,
      pos: [-1, top],
      anchor: [0, 0],
      rects: [
        [-1, top, d, eps, [1, 1, 1, 0.6 * alpha]],
        [-1 + d - 0.003, top, 0.006, eps, [1, 1, 1, alpha]],
      ],
      m: attachMatrix(a, pivot, pivot),
      color: [1, 1, 1, alpha],
    });
  }

  // Name and level.
  {
    const bt = 1 / ar - 2.8 * eps + slide;
    const an = attach.get('name');
    const al = attach.get('level');
    const nameW = Math.min(measure(input.name, 'ui', 0.5), 0.8);
    const levelW = measure(input.level, 'ui', 0.5);
    const h = ascentOf('ui', 0.5);
    const namePivot: [number, number] = [-0.97, bt];
    const levelPivot: [number, number] = [0.97, bt];
    els.push({
      id: 'name',
      visible: show,
      text: input.name,
      font: 'ui',
      size: 0.5,
      pos: [-0.97, bt],
      anchor: [0, 1],
      maxWidth: 0.8,
      m: attachMatrix(an, namePivot, attachFix ? namePivot : [-0.97 + nameW / 2, bt - h / 2]),
      color: attachColor(an),
    });
    els.push({
      id: 'level',
      visible: show,
      text: input.level,
      font: 'ui',
      size: 0.5,
      pos: [0.97, bt],
      anchor: [1, 1],
      m: attachMatrix(al, levelPivot, attachFix ? levelPivot : [0.97 - levelW / 2, bt - h / 2]),
      color: attachColor(al),
    });
  }
  return els;
}

/** EngineNotesModule.hud: the HUD of the frame. */
export function buildHud(
  ctx: EngineCompileContext,
  states: readonly LineStateRt[],
  t: number,
  opts: FrameOptions,
  viewport: Viewport,
): HudDraw {
  const judge = opts.judge;
  let numbers: HudNumbers;
  if (judge === null) {
    const v = autoplayHudValues(ctx, t);
    numbers = { ...v, accuracy: 1, maxCombo: v.combo };
  } else numbers = judgedHudValues(ctx, t, judge);
  const elements = layoutHud(
    {
      ar: viewport.ar,
      p: opts.hudProgress,
      attachFix: ctx.pkg.useAttachUiFix,
      autoplay: judge === null,
      show: opts.showHud,
      t,
      trackLengthSec: opts.trackLengthSec,
      numbers,
      name: ctx.meta.name,
      level: ctx.meta.level,
      showAccuracy: showAccuracyOf(opts.player),
      measureText: textMeasurerOf(opts),
    },
    attachments(ctx, states),
  );
  return { elements, alpha: opts.resAlpha };
}
